import { useDeferredValue, useEffect, useMemo, useState } from "react";
import type { Project } from "../../leaderboard/types/index.ts";
import { getEvWorkstationProjectSummary } from "../api/ev-workstation-api.ts";
import {
  useApplyEvPriorityActionMutation,
  useCreateEvWorkflowEventMutation,
  useEvBriefQuery,
  useEvGoogleConnectionStatusQuery,
  useEvOwnerOptionsQuery,
  useEvProjectCallPrepStatusStream,
  useEvSummaryQuery,
  useEvWorkstationProjectsQuery,
  usePendingEvIntroContactProjectIds,
  usePendingEvWorkflowProjectIds,
  useRefreshEvGoogleConnectionMutation,
  useStartEvGoogleConnectMutation,
  useUpdateEvIntroContactMutation,
  useUpdateEvWorkflowMutation,
} from "../hooks/use-ev-workstation-queries.ts";
import { queueForLane, type WorkstationLane } from "../lib/workstation-lanes.ts";
import type { EvIntroContactUpdateInput, EvWorkflowPatch } from "../types/index.ts";
import { EvDailyBriefPanel } from "./EvDailyBriefPanel.tsx";
import { EvWorkflowFilters } from "./EvWorkflowFilters.tsx";
import { EvWorkflowTable } from "./EvWorkflowTable.tsx";
import { EvWorkstationTour } from "./tour/index.ts";
import { EvWorkstationDashboard } from "./v2/EvWorkstationDashboard.tsx";

interface EvWorkstationViewProps {
  onOpenProjectDetail: (
    project: Project,
    options?: { workflowMode?: boolean; detailView?: "full" | "call_prep" | "operating_summary" }
  ) => void;
  focusRequest?: {
    project: Project;
    nonce: number;
    warmContactKey?: string;
  } | null;
  onFocusApplied?: () => void;
  addToast: (type: "success" | "error" | "info", title: string, message: string) => void;
}

function CallPrepStreamSubscription({ projectId }: { projectId: string }) {
  useEvProjectCallPrepStatusStream(projectId, true);
  return null;
}

function formatDateTime(value: string | null): string {
  if (!value) return "--";
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? "--" : parsed.toLocaleString();
}

export function EvWorkstationView({
  onOpenProjectDetail,
  focusRequest,
  onFocusApplied,
  addToast,
}: EvWorkstationViewProps) {
  const [viewMode, setViewMode] = useState<"card" | "table">("card");
  const [lane, setLane] = useState<WorkstationLane>("in_touch");
  const [selectedProjectId, setSelectedProjectId] = useState<string | null>(null);
  const [projectSearch, setProjectSearch] = useState("");
  const [contactSearch, setContactSearch] = useState("");
  const [stage, setStage] = useState("all");
  const [priority, setPriority] = useState("all");
  const [owner, setOwner] = useState("all");
  const [scope, setScope] = useState("active");
  const [scoreBand, setScoreBand] = useState("all");
  const [deltaDirection, setDeltaDirection] = useState("all");
  const [priorityActionPendingKey, setPriorityActionPendingKey] = useState<string | null>(null);
  const [focusedProjectId, setFocusedProjectId] = useState<string | null>(null);
  const [focusedProjectHandle, setFocusedProjectHandle] = useState<string | null>(null);
  const [focusedWarmContactKey, setFocusedWarmContactKey] = useState<string | null>(null);
  const [focusedProjectSource, setFocusedProjectSource] = useState<
    "leaderboard" | "recent_update" | null
  >(null);
  const [tourOpen, setTourOpen] = useState(false);

  const deferredProjectSearch = useDeferredValue(projectSearch);
  const filters = {
    search: deferredProjectSearch,
    stage,
    priority,
    owner,
    scope,
    scoreBand,
    deltaDirection,
  };

  const projectsQuery = useEvWorkstationProjectsQuery(true, filters);
  const summaryQuery = useEvSummaryQuery(true);
  const briefQuery = useEvBriefQuery(true);
  const googleConnectionQuery = useEvGoogleConnectionStatusQuery(true);
  const ownersQuery = useEvOwnerOptionsQuery(true);
  const updateMutation = useUpdateEvWorkflowMutation({
    onError: (error) => {
      addToast(
        "error",
        "Workflow update failed",
        error.message || "Could not save the workstation change."
      );
    },
  });
  const introContactMutation = useUpdateEvIntroContactMutation({
    onError: (error) => {
      addToast(
        "error",
        "Intro status update failed",
        error.message || "Could not save the intro contact change."
      );
    },
  });
  const workflowSavingProjectIds = usePendingEvWorkflowProjectIds();
  const introSavingProjectIds = usePendingEvIntroContactProjectIds();
  const savingProjectIds = useMemo(
    () => new Set([...workflowSavingProjectIds, ...introSavingProjectIds]),
    [introSavingProjectIds, workflowSavingProjectIds]
  );
  const startGoogleConnectMutation = useStartEvGoogleConnectMutation();
  const refreshGoogleConnectionMutation = useRefreshEvGoogleConnectionMutation();
  const createWorkflowEventMutation = useCreateEvWorkflowEventMutation();
  const applyPriorityActionMutation = useApplyEvPriorityActionMutation();

  const items = projectsQuery.data ?? [];
  const summary = summaryQuery.data ?? null;
  const brief = briefQuery.data ?? null;
  const googleConnectionStatus = googleConnectionQuery.data ?? null;
  const googleConnection = googleConnectionStatus?.connection ?? null;
  const googleNeedsReconnect =
    googleConnection?.connectionStatus === "reconnect_required" ||
    googleConnection?.connectionStatus === "revoked";
  const shouldShowGoogleBanner = Boolean(
    googleConnectionStatus &&
      (!googleConnectionStatus.connected ||
        googleNeedsReconnect ||
        !googleConnectionStatus.oauthConfigured ||
        !googleConnectionStatus.featureEnabled)
  );
  const hasGoogleConnection = Boolean(googleConnectionStatus?.connected || googleNeedsReconnect);
  const canStartGoogleConnect = Boolean(
    googleConnectionStatus?.oauthConfigured && googleConnectionStatus.featureEnabled
  );
  const shouldShowGoogleCompact = shouldShowGoogleBanner || viewMode === "table";
  const latestGoogleRun = googleConnectionStatus?.recentRuns?.[0] ?? null;
  const owners = ownersQuery.data ?? [];
  const projectsError = projectsQuery.error instanceof Error ? projectsQuery.error.message : null;
  const activeCallPrepProjectIds = useMemo(
    () =>
      Array.from(
        new Set(
          (brief?.calls ?? [])
            .filter((item) => item.status === "generating")
            .map((item) => item.projectId)
        )
      ),
    [brief]
  );
  const leadItems = useMemo(() => queueForLane(items, "leads"), [items]);
  const inTouchItems = useMemo(() => queueForLane(items, "in_touch"), [items]);
  const activeQueue = lane === "leads" ? leadItems : inTouchItems;
  const activeItem =
    activeQueue.find((item) => item.project.id === selectedProjectId) ?? activeQueue[0] ?? null;
  const googleBannerTitle =
    googleConnectionStatus && !googleConnectionStatus.featureEnabled
      ? "Google communications disabled"
      : !googleConnectionStatus?.oauthConfigured
        ? "Google OAuth not configured"
        : googleConnectionStatus.connected
          ? `Connected as ${googleConnection?.googleEmail ?? "Google account"}`
          : googleNeedsReconnect
            ? "Reconnect Google"
            : "Connect Google";
  const googleBannerMessage =
    googleConnectionStatus && !googleConnectionStatus.featureEnabled
      ? `Google Gmail and Calendar ingestion is disabled for this environment. Stored communication content is retained for ${googleConnectionStatus.retentionDays} days when enabled.`
      : !googleConnectionStatus?.oauthConfigured
        ? "Set the Google OAuth client, redirect URI, state secret, and token encryption key before connecting Gmail and Calendar."
        : googleConnectionStatus.connected
          ? `Last Gmail sync: ${formatDateTime(googleConnection?.lastGmailSyncAt ?? null)}. Last Calendar sync: ${formatDateTime(googleConnection?.lastCalendarSyncAt ?? null)}.`
          : googleNeedsReconnect
            ? (googleConnection?.lastError ??
              "Reconnect Google to restore Gmail and Calendar sync for this workstation.")
            : googleConnection?.lastError
              ? googleConnection.lastError
              : "Link a Google account to feed Gmail and Calendar data into the workstation.";

  useEffect(() => {
    if (!focusRequest) return;

    setProjectSearch(focusRequest.project.twitter_handle);
    setStage("all");
    setPriority("all");
    setOwner("all");
    setScope("all");
    setScoreBand("all");
    setDeltaDirection("all");
    setSelectedProjectId(focusRequest.project.id);
    setFocusedProjectId(focusRequest.project.id);
    setFocusedProjectHandle(focusRequest.project.twitter_handle);
    setFocusedProjectSource("leaderboard");
    setFocusedWarmContactKey(focusRequest.warmContactKey ?? null);
    onFocusApplied?.();
  }, [focusRequest, onFocusApplied]);

  useEffect(() => {
    if (!activeItem) return;
    if (selectedProjectId === activeItem.project.id) return;
    setSelectedProjectId(activeItem.project.id);
  }, [activeItem, selectedProjectId]);

  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.origin !== window.location.origin) return;
      const payload = event.data as { type?: string; status?: string; message?: string } | null;
      if (!payload || payload.type !== "legion-google-oauth") return;

      void googleConnectionQuery.refetch();
      addToast(
        payload.status === "success" ? "success" : "error",
        payload.status === "success" ? "Google connected" : "Google connection failed",
        payload.message ??
          (payload.status === "success"
            ? "Google account linked."
            : "The Google connection flow did not complete.")
      );
    };

    window.addEventListener("message", handleMessage);
    return () => {
      window.removeEventListener("message", handleMessage);
    };
  }, [addToast, googleConnectionQuery]);

  const projectLookup = useMemo(() => {
    const lookup = new Map<string, Project>();
    for (const item of items) {
      lookup.set(item.project.id, item.project);
    }
    for (const collection of [
      brief?.overdue,
      brief?.dueToday,
      brief?.missingNextAction,
      brief?.warmLeadsNeedingOutreach,
    ]) {
      for (const item of collection ?? []) {
        lookup.set(item.project.id, item.project);
      }
    }
    for (const item of brief?.calls ?? []) {
      const existing = items.find((project) => project.project.id === item.projectId);
      if (existing) {
        lookup.set(existing.project.id, existing.project);
      }
    }
    return lookup;
  }, [items, brief]);

  function pinProject(projectId: string, source: "leaderboard" | "recent_update") {
    const project = projectLookup.get(projectId);
    setFocusedProjectId(projectId);
    setFocusedProjectHandle(project?.twitter_handle ?? null);
    setFocusedProjectSource(source);
    setSelectedProjectId(projectId);
  }

  const focusBannerTitle =
    focusedProjectSource === "recent_update" ? "Pinned After Update" : "Focused from Leaderboard";
  const focusBannerMeta =
    focusedProjectSource === "recent_update"
      ? "Keeps the edited row visible while the queue reorders."
      : null;

  async function openProjectById(
    projectId: string,
    options?: { detailView?: "full" | "call_prep" | "operating_summary" }
  ) {
    const project = projectLookup.get(projectId);
    if (project) {
      onOpenProjectDetail(project, {
        workflowMode: true,
        detailView: options?.detailView,
      });
      return;
    }

    const summary = await getEvWorkstationProjectSummary(projectId);
    if (!summary?.project) return;
    onOpenProjectDetail(summary.project, {
      workflowMode: true,
      detailView: options?.detailView,
    });
  }

  async function handleStartGoogleConnect() {
    if (googleConnectionStatus && !googleConnectionStatus.featureEnabled) {
      addToast(
        "info",
        "Google communications disabled",
        "This environment has the Google communications feature flag turned off."
      );
      return;
    }

    if (!googleConnectionStatus?.oauthConfigured) {
      addToast(
        "error",
        "Google OAuth unavailable",
        "This environment is missing the required Google OAuth configuration."
      );
      return;
    }

    const popup = window.open(
      "about:blank",
      `legion-google-connect-${Date.now()}`,
      "popup,width=640,height=760"
    );
    if (!popup) {
      addToast(
        "error",
        "Popup blocked",
        "Allow popups for Legion Scout to complete the Google connection flow."
      );
      return;
    }

    try {
      const payload = await startGoogleConnectMutation.mutateAsync();
      const authorizationUrl = payload?.authorizationUrl;
      if (!authorizationUrl) {
        throw new Error("Google authorization URL was not returned");
      }
      if (popup.closed) {
        throw new Error("Google authorization popup was closed before navigation");
      }
      popup.location.href = authorizationUrl;
    } catch (error) {
      popup.close();
      addToast(
        "error",
        "Google connect failed",
        error instanceof Error ? error.message : "Could not start the Google OAuth flow."
      );
    }
  }

  function goNext() {
    if (!activeItem) return;
    const index = activeQueue.findIndex((item) => item.project.id === activeItem.project.id);
    const next = activeQueue[index + 1] ?? activeQueue[0] ?? null;
    setSelectedProjectId(next?.project.id ?? null);
  }

  async function handleUpdateWorkflow(projectId: string, patch: EvWorkflowPatch) {
    pinProject(projectId, "recent_update");
    await updateMutation.mutateAsync({ projectId, patch });
  }

  async function handleUpdateIntroContact(projectId: string, input: EvIntroContactUpdateInput) {
    pinProject(projectId, "recent_update");
    await introContactMutation.mutateAsync({ projectId, input });
  }

  const todayBriefPanel = (
    <EvDailyBriefPanel
      brief={brief}
      loading={briefQuery.isLoading}
      onOpenProject={openProjectById}
      priorityActionPendingKey={priorityActionPendingKey}
      onApplyPriorityAction={async (projectId, input) => {
        pinProject(projectId, "recent_update");
        setPriorityActionPendingKey(input.priorityKey);

        try {
          await applyPriorityActionMutation.mutateAsync({ projectId, input });
          addToast(
            "success",
            input.action === "snooze"
              ? "Priority snoozed"
              : input.action === "resolve"
                ? "Priority resolved"
                : "Priority marked blocked",
            input.action === "snooze"
              ? `This priority will stay out of today's brief until ${input.snoozedUntil ?? "the new date"} unless new signals arrive.`
              : input.action === "resolve"
                ? "The priority was marked resolved and will only return if new evidence reactivates it."
                : "The priority stays visible with your blocker note attached for EV and the agent."
          );
        } catch (error) {
          addToast(
            "error",
            "Priority action failed",
            error instanceof Error ? error.message : "Could not apply the priority action."
          );
          throw error;
        } finally {
          setPriorityActionPendingKey((current) =>
            current === input.priorityKey ? null : current
          );
        }
      }}
    />
  );

  const googleStatusNode = (
    <div className="ev-v2-google-status">
      <span className="section-title">
        {googleConnectionStatus?.connected ? "Google connected" : googleBannerTitle}
      </span>
      <span>{googleBannerMessage}</span>
      {hasGoogleConnection && googleConnection ? (
        <span className="mono">
          Status: {googleConnection.connectionStatus} / {googleConnection.syncHealthStatus}
        </span>
      ) : null}
      {googleConnectionStatus ? (
        <span>
          Feature: {googleConnectionStatus.featureEnabled ? "enabled" : "disabled"} / retention:{" "}
          {googleConnectionStatus.retentionDays} days
        </span>
      ) : null}
      {latestGoogleRun ? (
        <span>
          Latest sync: {latestGoogleRun.provider} / {latestGoogleRun.status} /{" "}
          {formatDateTime(latestGoogleRun.finishedAt ?? latestGoogleRun.startedAt)}
        </span>
      ) : null}
      <div className="ev-google-banner__actions">
        <button
          type="button"
          className="btn btn-small btn-secondary"
          disabled={googleConnectionQuery.isFetching}
          onClick={() => {
            void googleConnectionQuery.refetch();
          }}
        >
          {googleConnectionQuery.isFetching ? "Refreshing..." : "Refresh"}
        </button>
        {hasGoogleConnection ? (
          <button
            type="button"
            className="btn btn-small btn-secondary"
            disabled={
              refreshGoogleConnectionMutation.isPending || !googleConnectionStatus?.featureEnabled
            }
            onClick={() => {
              refreshGoogleConnectionMutation.mutate("all", {
                onSuccess: () => {
                  addToast(
                    "info",
                    "Google sync queued",
                    "Queued Gmail and Calendar refresh jobs for this account."
                  );
                },
                onError: (error) => {
                  addToast(
                    "error",
                    "Google sync failed",
                    error instanceof Error ? error.message : "Could not queue a Google refresh."
                  );
                },
              });
            }}
          >
            {refreshGoogleConnectionMutation.isPending ? "Queueing..." : "Sync Now"}
          </button>
        ) : null}
      </div>
    </div>
  );

  return (
    <div
      className={`ev-workstation-view ev-workstation-view--${viewMode}`}
      data-ev-tour="workstation-root"
    >
      {activeCallPrepProjectIds.map((projectId) => (
        <CallPrepStreamSubscription key={projectId} projectId={projectId} />
      ))}

      {focusedProjectId && focusedProjectHandle && (
        <div className="ev-focus-banner" data-ev-tour="focus-banner">
          <div className="ev-focus-banner__copy">
            <span className="section-title">{focusBannerTitle}</span>
            <span className="mono">@{focusedProjectHandle}</span>
            {focusBannerMeta ? (
              <span className="ev-google-banner__meta">{focusBannerMeta}</span>
            ) : null}
          </div>
          <button
            type="button"
            className="btn btn-small btn-secondary"
            onClick={() => {
              const shouldResetFilters = focusedProjectSource === "leaderboard";
              setFocusedProjectId(null);
              setFocusedProjectHandle(null);
              setFocusedProjectSource(null);
              setFocusedWarmContactKey(null);
              if (shouldResetFilters) {
                setProjectSearch("");
                setScope("active");
              }
            }}
          >
            Clear Focus
          </button>
        </div>
      )}

      <div className="ev-view-mode-toggle" data-ev-tour="workstation-view-toolbar">
        <div>
          <span className="section-title">Workstation View</span>
          <span className="ev-v2-muted">
            Default is the two-lane card workflow. Table stays as fallback during rollout.
          </span>
        </div>
        <div className="ev-v2-actions-row">
          {shouldShowGoogleCompact ? (
            <div
              className="ev-google-compact"
              title={googleBannerMessage}
              data-ev-tour="google-compact-status"
            >
              <span
                className={`ev-google-compact__dot${canStartGoogleConnect ? " is-attention" : ""}`}
                aria-hidden="true"
              />
              <span className="ev-google-compact__label">Google</span>
              <span className="ev-google-compact__status">{googleBannerTitle}</span>
              {shouldShowGoogleBanner && canStartGoogleConnect ? (
                <button
                  type="button"
                  className="btn btn-small btn-secondary"
                  disabled={startGoogleConnectMutation.isPending}
                  onClick={() => {
                    void handleStartGoogleConnect();
                  }}
                >
                  {startGoogleConnectMutation.isPending
                    ? "Opening..."
                    : hasGoogleConnection
                      ? "Reconnect"
                      : "Connect"}
                </button>
              ) : null}
              {viewMode === "table" && hasGoogleConnection ? (
                <button
                  type="button"
                  className="btn btn-small btn-secondary"
                  disabled={
                    refreshGoogleConnectionMutation.isPending ||
                    !googleConnectionStatus?.featureEnabled
                  }
                  onClick={() => {
                    refreshGoogleConnectionMutation.mutate("all", {
                      onSuccess: () => {
                        addToast(
                          "info",
                          "Google sync queued",
                          "Queued Gmail and Calendar refresh jobs for this account."
                        );
                      },
                      onError: (error) => {
                        addToast(
                          "error",
                          "Google sync failed",
                          error instanceof Error
                            ? error.message
                            : "Could not queue a Google refresh."
                        );
                      },
                    });
                  }}
                >
                  {refreshGoogleConnectionMutation.isPending ? "Queueing..." : "Sync"}
                </button>
              ) : null}
              <button
                type="button"
                className="ev-google-compact__refresh"
                disabled={googleConnectionQuery.isFetching}
                title="Refresh Google status"
                aria-label="Refresh Google status"
                onClick={() => {
                  void googleConnectionQuery.refetch();
                }}
              >
                {googleConnectionQuery.isFetching ? "…" : "↻"}
              </button>
            </div>
          ) : null}
          <button
            type="button"
            className="btn btn-small btn-secondary"
            data-ev-tour="guide-button"
            onClick={() => setTourOpen(true)}
          >
            Guide
          </button>
          <button
            type="button"
            className={`btn btn-small ${viewMode === "card" ? "" : "btn-secondary"}`}
            data-ev-tour="card-view-toggle"
            onClick={() => setViewMode("card")}
          >
            Card view
          </button>
          <button
            type="button"
            className={`btn btn-small ${viewMode === "table" ? "" : "btn-secondary"}`}
            data-ev-tour="table-view-toggle"
            onClick={() => setViewMode("table")}
          >
            Table fallback
          </button>
        </div>
      </div>

      {projectsQuery.isLoading ? (
        <div className="trends-section">
          <div className="loading">
            <div className="spinner" />
            <span className="loading-text">Loading workstation</span>
          </div>
        </div>
      ) : projectsError ? (
        <div className="community-empty queue">Workstation failed to load: {projectsError}</div>
      ) : viewMode === "card" ? (
        <EvWorkstationDashboard
          lane={lane}
          onLaneChange={(nextLane) => {
            setLane(nextLane);
            setSelectedProjectId(null);
          }}
          queue={activeQueue}
          leadCount={leadItems.length}
          inTouchCount={inTouchItems.length}
          activeItem={activeItem}
          savingProjectIds={savingProjectIds}
          projectSearch={projectSearch}
          onProjectSearchChange={setProjectSearch}
          contactSearch={contactSearch}
          onContactSearchChange={setContactSearch}
          onSelectProject={setSelectedProjectId}
          onNext={goNext}
          onOpenProjectDetail={onOpenProjectDetail}
          onUpdateWorkflow={handleUpdateWorkflow}
          onUpdateIntroContact={handleUpdateIntroContact}
          onCreateWorkflowEvent={async (projectId, event) => {
            pinProject(projectId, "recent_update");
            await createWorkflowEventMutation.mutateAsync({ projectId, event });
          }}
          todayBriefNode={todayBriefPanel}
          googleStatusNode={googleStatusNode}
          addToast={addToast}
        />
      ) : (
        <>
          {todayBriefPanel}
          <EvWorkflowFilters
            search={projectSearch}
            stage={stage}
            priority={priority}
            owner={owner}
            scope={scope}
            scoreBand={scoreBand}
            deltaDirection={deltaDirection}
            summary={summary}
            owners={owners}
            onSearchChange={setProjectSearch}
            onStageChange={setStage}
            onPriorityChange={setPriority}
            onOwnerChange={setOwner}
            onScopeChange={setScope}
            onScoreBandChange={setScoreBand}
            onDeltaDirectionChange={setDeltaDirection}
          />

          {items.length === 0 ? (
            <div className="community-empty queue">
              No workstation rows matched the current filters.
            </div>
          ) : (
            <EvWorkflowTable
              items={items}
              savingProjectIds={savingProjectIds}
              focusedProjectId={focusedProjectId}
              focusedWarmContactKey={focusedWarmContactKey}
              onOpenProjectDetail={onOpenProjectDetail}
              onUpdateWorkflow={(projectId, patch) => {
                void handleUpdateWorkflow(projectId, patch);
              }}
              onUpdateIntroContact={(projectId, input) => {
                void handleUpdateIntroContact(projectId, input);
              }}
              onSaveActionPlan={async (projectId, input) => {
                pinProject(projectId, "recent_update");

                const currentRow = items.find((entry) => entry.project.id === projectId);
                const currentNextAction = currentRow?.workflow.nextAction ?? null;

                if (input.nextAction !== currentNextAction) {
                  await updateMutation.mutateAsync({
                    projectId,
                    patch: { nextAction: input.nextAction },
                  });
                }

                if (input.note) {
                  await createWorkflowEventMutation.mutateAsync({
                    projectId,
                    event: {
                      eventType: "note_added",
                      note: input.note,
                    },
                  });
                }
              }}
            />
          )}
        </>
      )}

      <EvWorkstationTour
        open={tourOpen}
        onClose={() => setTourOpen(false)}
        viewMode={viewMode}
        setViewMode={setViewMode}
        lane={lane}
        setLane={setLane}
        selectedProjectId={selectedProjectId}
        setSelectedProjectId={setSelectedProjectId}
        hasProjects={items.length > 0}
      />
    </div>
  );
}
